import {
  colors,
  ImpButton,
  ImpCard,
  ImpContainer,
  ImpDivider,
  ImpFlex,
  ImpModal,
  ImpModalHeader,
  ImpPad,
  ImpSpacer,
  ImpSurface,
  ImpText,
  spacing,
  useModal,
} from '@imprint-payments/imprint-ui';
import { useStatsigClient } from '@statsig/react-bindings';
import QRCode from 'qrcode.react';
import { useEffect } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { useImpState } from '../../app/hooks';
import {
  getLandingPageUrl,
  IMPRINT_STORE_HELP_URL,
  PARAM_SHOW_WPP,
} from '../../assets/consts/inStoreConsts';
import { type LandingPageURLKey } from '../../assets/consts/inStoreConsts';
import { ANALYTICS } from '../../firebase/firebase';
import Gates from '../../statsig/gates';
import { getDomainFromMerchant } from '../../utils/environments';
import { SESSION_STORAGE } from '../../utils/sessionStorageConstants';

type InStoreModalProps = {
  sourcePage: 'signup' | 'offer' | 'existing';
};

export const navigateToLP = (productUUID: LandingPageURLKey) => {
  window.location.href = getLandingPageUrl(productUUID);
};

function InStoreModal({ sourcePage }: InStoreModalProps) {
  const [isInStoreModalOpen, isInStoreModalClosing, _, closeInStoreModal] =
    useModal(true);
  return (
    <ImpModal isOpen={isInStoreModalOpen} isClosing={isInStoreModalClosing}>
      {
        {
          signup: <SignupModal closeModal={closeInStoreModal} />,
          offer: <OfferModal />,
          existing: <WelcomeBackModal />,
        }[sourcePage]
      }
    </ImpModal>
  );
}

const SignupModal = ({ closeModal }: { closeModal: () => void }) => {
  const { merchantData, language } = useImpState();

  useEffect(() => {
    ANALYTICS.logEvent(EVENT.signupModalViewed);
    return () => ANALYTICS.logEvent(EVENT.signupModalClosed);
  }, []);

  const qrLink: string =
    getDomainFromMerchant(merchantData.productUUID) +
    '?' +
    sessionStorage.getItem(SESSION_STORAGE.FULL_QUERY);
  console.log(`qrLink: ${qrLink}`);

  return (
    <ImpSurface fluid>
      <ImpPad paddingTop="xl" paddingBottom="xl">
        <ImpFlex
          gap="3xl"
          mobileGap="3xl"
          direction="column"
          fluid
          align="center"
        >
          <ImpPad fluid paddingLeft="xl" paddingRight="xl">
            <ImpModalHeader closeModal={closeModal} />
          </ImpPad>
          <ImpContainer
            backgroundColor={'var(--merchant-color)'}
            borderRadius={spacing.s}
          >
            <ImpPad padding={spacing.l}>
              <QRCode
                value={qrLink}
                size={132}
                fgColor={colors.content.onSurface}
              />
            </ImpPad>
          </ImpContainer>
          <ImpFlex align="center" direction="column" fluid>
            <ImpContainer textAlign="center">
              <ImpPad paddingLeft="xl" paddingRight="xl">
                <ImpText color={'var(--merchant-color)'} typography="headline3">
                  {language.forTheBestExp}
                </ImpText>
                <ImpSpacer height={spacing.s} />
                <ImpText color={'var(--merchant-color)'} typography="display3">
                  {language.scanQRApply}
                </ImpText>
              </ImpPad>
            </ImpContainer>
          </ImpFlex>
          <ImpFlex align="center" fluid direction="column">
            <ImpDivider color={'var(--merchant-color)'} />
            <ImpSpacer height={spacing.xl} />
            <ImpButton
              onClick={closeModal}
              variant="secondary"
              buttonStyle="branded"
            >
              {language.continueOnThisDevice}
            </ImpButton>
          </ImpFlex>
        </ImpFlex>
      </ImpPad>
    </ImpSurface>
  );
};

export const OfferModal = () => {
  const { merchantData, language } = useImpState();
  const { checkGate } = useStatsigClient();

  useEffect(() => {
    ANALYTICS.logEvent(EVENT.offerModalViewed);
  }, []);

  function makeLoginQrLink() {
    const loginUrl: string = getDomainFromMerchant(''); // login link
    const qrUrl = new URL(loginUrl);

    // Use URLSearchParams to manage the query parameters
    const params = new URLSearchParams(qrUrl.search);
    params.set(PARAM_SHOW_WPP, 'true'); // Add or update the parameter

    // Set the modified search parameters back to the URL
    qrUrl.search = params.toString();

    return qrUrl.toString();
  }

  const qrLink = checkGate(Gates.AddToWalletGate)
    ? makeLoginQrLink()
    : IMPRINT_STORE_HELP_URL;
  console.log(`qrLink: ${qrLink}`);

  return (
    <ImpSurface fluid>
      <ImpPad paddingTop="2xl" paddingBottom="xl">
        <ImpFlex
          gap="3xl"
          mobileGap="3xl"
          direction="column"
          fluid
          align="center"
        >
          <ImpContainer
            backgroundColor={'var(--merchant-color)'}
            borderRadius={spacing.s}
          >
            <ImpPad padding={spacing.l}>
              <QRCode
                value={qrLink}
                size={132}
                fgColor={colors.content.onSurface}
              />
            </ImpPad>
          </ImpContainer>
          <ImpCard
            heightPx={216}
            widthPx={343}
            borderRadiusPx={8}
            src={merchantData.cardImg}
            hoverable={true}
            shimmer
          />
          <ImpFlex align="center" direction="column" fluid>
            <ImpContainer textAlign="center">
              <ImpPad paddingLeft="xl" paddingRight="xl">
                <ImpText color={'var(--merchant-color)'} typography="display3">
                  {language.scanQRUseCardNow}
                </ImpText>
                <ImpSpacer height={spacing.s} />
                <ImpText color={'var(--merchant-color)'} typography="headline3">
                  {language.orClickTheLink}
                </ImpText>
              </ImpPad>
            </ImpContainer>
          </ImpFlex>
          <ImpFlex align="center" fluid direction="column">
            <ImpDivider color={'var(--merchant-color)'} />
            <ImpSpacer height={spacing.xl} />
            <ImpButton
              onClick={() =>
                navigateToLP(merchantData.productUUID as LandingPageURLKey)
              }
              variant="secondary"
              buttonStyle="color"
              color={'var(--merchant-color)'}
            >
              {language.goBackToLP}
            </ImpButton>
          </ImpFlex>
        </ImpFlex>
      </ImpPad>
    </ImpSurface>
  );
};

const WelcomeBackModal = () => {
  const { merchantData, language } = useImpState();
  const qrLink = getDomainFromMerchant('');

  useEffect(() => {
    ANALYTICS.logEvent(EVENT.welcomeBackModalViewed);
  }, []);
  console.log(`qrLink: ${qrLink}`);

  return (
    <ImpSurface fluid>
      <ImpPad paddingTop="2xl" paddingBottom="xl">
        <ImpFlex
          gap="3xl"
          mobileGap="3xl"
          direction="column"
          fluid
          align="center"
        >
          <ImpContainer
            backgroundColor={'var(--merchant-color)'}
            borderRadius={spacing.s}
          >
            <ImpPad padding={spacing.l}>
              <QRCode
                value={qrLink}
                size={132}
                fgColor={colors.content.onSurface}
              />
            </ImpPad>
          </ImpContainer>
          <ImpCard
            heightPx={216}
            widthPx={343}
            borderRadiusPx={8}
            src={merchantData.cardImg}
            shimmer
          />
          <ImpFlex align="center" direction="column" fluid>
            <ImpContainer textAlign="center">
              <ImpPad paddingLeft="xl" paddingRight="xl">
                <ImpText color={'var(--merchant-color)'} typography="display3">
                  {language.welcomeBackExclamation}
                </ImpText>
                <ImpSpacer height={spacing.s} />
                <ImpText
                  color={'var(--merchant-color)'}
                  typography="headline3"
                  handleSuperscript
                >
                  {language.thankYouFor.replace(
                    '$cardName$',
                    merchantData.rewardDetails.cardName
                  )}
                </ImpText>
              </ImpPad>
            </ImpContainer>
          </ImpFlex>
          <ImpFlex align="center" fluid direction="column">
            <ImpDivider color={'var(--merchant-color)'} />
            <ImpSpacer height={spacing.xl} />
            <ImpButton
              onClick={() =>
                navigateToLP(merchantData.productUUID as LandingPageURLKey)
              }
              variant="secondary"
              buttonStyle="color"
              color={'var(--merchant-color)'}
            >
              {language.goBackToLP}
            </ImpButton>
          </ImpFlex>
        </ImpFlex>
      </ImpPad>
    </ImpSurface>
  );
};

export default InStoreModal;
